import { useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';

const searchDelayMilliseconds = 250;

export interface RemoteOption {
  id: string;
  label: string;
}

interface RemoteSelectProps {
  label: string;
  noMatchesText: string;
  /** Loads the options for the text typed so far; a failure simply shows no options. */
  load: (text: string) => Promise<RemoteOption[]>;
  /** Called with the chosen option, or empty strings when the choice is cleared. */
  onSelect: (identifier: string, label: string) => void;
  /** Whether the surrounding form needs a choice before it can be submitted (default: yes). */
  required?: boolean;
}

/** A search-as-you-type picker: the server filters, so a large club never loads in full. */
export function RemoteSelect({
  label,
  noMatchesText,
  load,
  onSelect,
  required = true,
}: RemoteSelectProps): React.JSX.Element {
  const [text, setText] = useState('');
  const [options, setOptions] = useState<RemoteOption[]>([]);

  useEffect(() => {
    if (text.trim() === '') return;
    let cancelled = false;
    const timer = setTimeout(() => {
      load(text.trim()).then(
        (found) => {
          if (!cancelled) setOptions(found);
        },
        () => {
          if (!cancelled) setOptions([]);
        },
      );
    }, searchDelayMilliseconds);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [text, load]);

  return (
    <Autocomplete
      size="small"
      sx={{ minWidth: 260, flexGrow: 1 }}
      options={text.trim() === '' ? [] : options}
      // The server already matched the text; the client must not filter the options a second time.
      filterOptions={(candidates) => candidates}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      noOptionsText={noMatchesText}
      onInputChange={(_event, value) => {
        setText(value);
      }}
      onChange={(_event, option) => {
        onSelect(option?.id ?? '', option?.label ?? '');
      }}
      renderInput={(parameters) => <TextField {...parameters} required={required} label={label} />}
    />
  );
}
